import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  FsnFloristCard,
  FsnIncomingOrder,
  FsnIncomingOrderAcceptPayload,
  FsnIncomingOrderAcknowledgeChangePayload,
  FsnIncomingOrderAddressCheck,
  FsnIncomingOrderAddressInput,
  FsnIncomingOrderDetail,
  FsnIncomingOrderFilters,
  FsnIncomingOrderFinishChangePayload,
  FsnIncomingOrderRefusePayload,
  FsnIncomingOrdersRefresh,
  FsnIncomingOrdersResponse,
  FsnNetworkIntegration,
  FsnNetworkIntegrationInput,
  FsnNetworkIntegrationResponse,
  FsnNetworkIntegrationWithReadiness,
  FsnNetworkLoginInput,
  FsnNetworkTokenCheck,
  FsnSalesOrderFlorists,
  FsnSalesOrderFulfillmentResponse,
  FsnSalesOrderSubmitPayload,
} from './fsn-network.types'

/** Route params arrive as strings; payloads carry numbers. */
export type FsnIncomingOrderIdInput = number | string

interface FsnNetworkOrganizationScope {
  organizationId: OrganizationId
}

// Query key entries. The scoped key builders always set the organization; they can't mark the
// other entries required.
export interface FsnNetworkIntegrationKeyEntries extends FsnNetworkOrganizationScope {
  includeReadiness?: boolean
}

export interface FsnSalesOrderFloristsKeyEntries extends FsnNetworkOrganizationScope {
  salesOrderId?: SalesOrderIdInput | null
}

export interface FsnIncomingOrdersKeyEntries extends FsnNetworkOrganizationScope {
  filters?: FsnIncomingOrderFilters | undefined
}

export interface FsnIncomingOrderKeyEntries extends FsnNetworkOrganizationScope {
  incomingOrderId?: FsnIncomingOrderIdInput
}

/** Scope for reading one incoming order; the Laravel route requires its id. */
export interface FsnIncomingOrderScope extends FsnNetworkOrganizationScope {
  incomingOrderId: FsnIncomingOrderIdInput
}

/** Calls that change the organization's FSN connection or orders. */
interface FsnNetworkTarget {
  organizationId: number | string
}

interface FsnSalesOrderTarget extends FsnNetworkTarget {
  salesOrderId: SalesOrderIdInput
}

interface FsnIncomingOrderTarget extends FsnNetworkTarget {
  incomingOrderId: FsnIncomingOrderIdInput
}

/** Query functions read the key, and the abort signal when the caller passes one. */
type FsnNetworkQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'> &
  Partial<Pick<QueryFunctionContext<[TKey]>, 'signal'>>

const inFlightReadinessRequests = new Map<string, Promise<FsnNetworkIntegrationWithReadiness>>()

export function resetFsnNetworkReadinessRequest(organizationId: number | string) {
  inFlightReadinessRequests.delete(String(organizationId))
}

export async function getFsnNetworkIntegration({
  queryKey: [{ organizationId, includeReadiness }],
  signal,
}: FsnNetworkQueryContext<FsnNetworkIntegrationKeyEntries>): Promise<FsnNetworkIntegrationResponse> {
  const requestConfig = buildAdminRequestConfig({
    organizationId,
    ...(includeReadiness ? { params: { include_readiness: true } } : {}),
    ...(!includeReadiness && signal ? { signal } : {}),
  })

  if (!includeReadiness) {
    return (await axios.get<FsnNetworkIntegration>('/fsn-network/integration', requestConfig)) || {}
  }

  const requestKey = String(organizationId)
  const existingRequest = inFlightReadinessRequests.get(requestKey)

  if (existingRequest) {
    return (await existingRequest) || {}
  }

  // Keep the slow background check single-flight across development remounts.
  const request = axios.get<FsnNetworkIntegrationWithReadiness>(
    '/fsn-network/integration',
    requestConfig
  )
  inFlightReadinessRequests.set(requestKey, request)

  try {
    return (await request) || {}
  } finally {
    if (inFlightReadinessRequests.get(requestKey) === request) {
      inFlightReadinessRequests.delete(requestKey)
    }
  }
}

export async function updateFsnNetworkIntegration({
  organizationId,
  ...values
}: FsnNetworkTarget & FsnNetworkIntegrationInput) {
  return axios.put<FsnNetworkIntegrationResponse>(
    '/fsn-network/integration',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function connectFsnNetworkWithLogin({
  organizationId,
  username,
  password,
}: FsnNetworkTarget & FsnNetworkLoginInput) {
  return axios.post<FsnNetworkIntegrationWithReadiness>(
    '/fsn-network/token/connect',
    { username, password },
    buildAdminRequestConfig({ organizationId })
  )
}

/** Checks the given token, or the saved one when none is given. */
export async function checkFsnNetworkToken({
  organizationId,
  apiToken,
}: FsnNetworkTarget & { apiToken?: string }) {
  return axios.post<FsnNetworkTokenCheck>(
    '/fsn-network/token/check',
    apiToken ? { api_token: apiToken } : {},
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getFsnNetworkSalesOrderFlorists({
  queryKey: [{ organizationId, salesOrderId }],
  signal,
}: FsnNetworkQueryContext<FsnSalesOrderFloristsKeyEntries>) {
  return (
    (await axios.get<FsnSalesOrderFlorists>(
      `/fsn-network/sales-orders/${salesOrderId}/florists`,
      buildAdminRequestConfig({
        organizationId,
        ...(signal ? { signal } : {}),
      })
    )) || {}
  )
}

export interface SubmitFsnNetworkSalesOrderInput {
  salesOrderId: SalesOrderIdInput
  /** Left out to let FSN choose the filling florist. */
  receivingFloristId?: string
  requestedFlorist?: FsnFloristCard
}

export async function submitFsnNetworkSalesOrder({
  organizationId,
  salesOrderId,
  receivingFloristId,
  requestedFlorist,
}: FsnNetworkTarget & SubmitFsnNetworkSalesOrderInput) {
  const payload: FsnSalesOrderSubmitPayload = {
    ...(receivingFloristId ? { receiving_florist_id: receivingFloristId } : {}),
    ...(requestedFlorist ? { requested_florist: requestedFlorist } : {}),
  }

  return axios.post<FsnSalesOrderFulfillmentResponse>(
    `/fsn-network/sales-orders/${salesOrderId}/submit`,
    Object.keys(payload).length ? payload : null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function refreshFsnNetworkSalesOrder({
  organizationId,
  salesOrderId,
}: FsnSalesOrderTarget) {
  return axios.post<FsnSalesOrderFulfillmentResponse>(
    `/fsn-network/sales-orders/${salesOrderId}/refresh`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getFsnIncomingOrders({
  queryKey: [{ organizationId, filters = {} }],
  signal,
}: FsnNetworkQueryContext<FsnIncomingOrdersKeyEntries>) {
  return (
    (await axios.get<FsnIncomingOrdersResponse>(
      '/fsn-network/incoming-orders',
      buildAdminRequestConfig({
        organizationId,
        params: { ...filters },
        ...(signal ? { signal } : {}),
      })
    )) || { data: [], summary: {}, refusal_reasons: {} }
  )
}

export async function getFsnIncomingOrder({
  queryKey: [{ organizationId, incomingOrderId }],
  signal,
}: FsnNetworkQueryContext<FsnIncomingOrderKeyEntries>) {
  return axios.get<FsnIncomingOrderDetail>(
    `/fsn-network/incoming-orders/${incomingOrderId}`,
    buildAdminRequestConfig({
      organizationId,
      ...(signal ? { signal } : {}),
    })
  )
}

export async function refreshFsnIncomingOrders({ organizationId }: FsnNetworkTarget) {
  return axios.post<FsnIncomingOrdersRefresh>(
    '/fsn-network/incoming-orders/refresh',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export type AcceptFsnIncomingOrderInput = FsnIncomingOrderAcceptPayload & {
  incomingOrderId: FsnIncomingOrderIdInput
}

export async function acceptFsnIncomingOrder({
  organizationId,
  incomingOrderId,
  ...review
}: FsnNetworkTarget & AcceptFsnIncomingOrderInput) {
  return axios.post<FsnIncomingOrder>(
    `/fsn-network/incoming-orders/${incomingOrderId}/accept`,
    review,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function retryFsnIncomingOrderImport({
  organizationId,
  incomingOrderId,
}: FsnIncomingOrderTarget) {
  return axios.post<FsnIncomingOrder>(
    `/fsn-network/incoming-orders/${incomingOrderId}/retry-import`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export interface CheckFsnIncomingOrderAddressInput {
  incomingOrderId: FsnIncomingOrderIdInput
  address: FsnIncomingOrderAddressInput
}

export async function checkFsnIncomingOrderAddress({
  organizationId,
  incomingOrderId,
  address,
}: FsnNetworkTarget & CheckFsnIncomingOrderAddressInput) {
  return axios.post<FsnIncomingOrderAddressCheck>(
    `/fsn-network/incoming-orders/${incomingOrderId}/address-check`,
    { address },
    buildAdminRequestConfig({ organizationId })
  )
}

/** The review fields go as sent; the reason is required and a blank note is left out. */
export type RefuseFsnIncomingOrderInput = Omit<
  FsnIncomingOrderRefusePayload,
  'refuse_reason_key' | 'refuse_reason_note'
> & {
  incomingOrderId: FsnIncomingOrderIdInput
  /** Raw form value; Laravel validates it against the generated payload enum. */
  refuseReasonKey: string
  refuseReasonNote?: string | null
}

export async function refuseFsnIncomingOrder({
  organizationId,
  incomingOrderId,
  refuseReasonKey,
  refuseReasonNote,
  ...review
}: FsnNetworkTarget & RefuseFsnIncomingOrderInput) {
  return axios.post<FsnIncomingOrder>(
    `/fsn-network/incoming-orders/${incomingOrderId}/refuse`,
    {
      ...review,
      refuse_reason_key: refuseReasonKey,
      ...(refuseReasonNote ? { refuse_reason_note: refuseReasonNote } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export type FinishFsnIncomingOrderChangeInput = FsnIncomingOrderFinishChangePayload & {
  incomingOrderId: FsnIncomingOrderIdInput
}

export async function finishFsnIncomingOrderChange({
  organizationId,
  incomingOrderId,
  version,
  review_token,
}: FsnNetworkTarget & FinishFsnIncomingOrderChangeInput) {
  return axios.post<FsnIncomingOrder>(
    `/fsn-network/incoming-orders/${incomingOrderId}/finish-change`,
    { version, review_token },
    buildAdminRequestConfig({ organizationId })
  )
}

export type AcknowledgeFsnIncomingOrderChangeInput = FsnIncomingOrderAcknowledgeChangePayload & {
  incomingOrderId: FsnIncomingOrderIdInput
}

export async function acknowledgeFsnIncomingOrderChange({
  organizationId,
  incomingOrderId,
  version,
}: FsnNetworkTarget & AcknowledgeFsnIncomingOrderChangeInput) {
  return axios.post<FsnIncomingOrder>(
    `/fsn-network/incoming-orders/${incomingOrderId}/acknowledge-change`,
    { version },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function refreshFsnIncomingOrder({
  organizationId,
  incomingOrderId,
}: FsnIncomingOrderTarget) {
  return axios.post<FsnIncomingOrder>(
    `/fsn-network/incoming-orders/${incomingOrderId}/refresh`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}
